import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import replaceQueryParameters from '@creuna/utils/replace-query-parameters';
import Pager from 'components/Pager';
import Loader from 'components/Loader';
import NoResults from 'components/NoResults';
import MarketInsightStatisticArchiveItem from './MarketInsightStatisticArchiveItem';
import api from '../../js/api-helper';
import formHelper from 'js/form-helper';
import useDidUpdateEffect from '../../hooks/use-did-update-effect';
import './MarketInsightStatisticArchiveList.scss';

const MarketInsightStatisticArchiveList = ({ loadItemsEndpoint, lang }) => {
  const [query, setQuery] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pager, setPager] = useState(null);
  const [archiveListItems, setArchiveListItems] = useState(null);

  const onPageChange = page => {
    setQuery(prevQuery => ({
      ...prevQuery,
      page: page.activePageNumber,
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadArchiveListItems = () => {
    setIsLoading(true);
    setError(null);
    api
      .get(replaceQueryParameters(loadItemsEndpoint, query))
      .then(response => {
        setIsLoading(false);
        if (response.success) {
          setArchiveListItems(response.data.items);
          setPager(response.data.pager);
        } else {
          setError(response.errorMessage);
        }
      })
      .catch(() => {
        setError(lang.serverErrorText);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    const queryParams = formHelper.getQueryParams();
    const initPageNumber =
      queryParams && queryParams.page ? queryParams.page : 1;

    setQuery(
      formHelper.fillQueryFromPageQueryString({
        page: parseInt(initPageNumber),
      })
    );
  }, []);

  useDidUpdateEffect(() => {
    formHelper.updatePageQueryString(query);
    loadArchiveListItems();
  }, [query]);

  return (
    <div className="market-insight-statistic-archive-list">
      {archiveListItems && archiveListItems.length > 0
        ? archiveListItems.map((item, index) => (
            <div
              key={index}
              className="market-insight-statistic-archive-list__item"
            >
              <MarketInsightStatisticArchiveItem
                pdfLabel={lang.pdfLabel}
                excelLabel={lang.excelLabel}
                {...item}
              />
            </div>
          ))
        : null}
      {archiveListItems && archiveListItems.length === 0 && (
        <NoResults
          labels={{
            noResultsHeading: lang.noListResultsHeading,
            noResultsText: lang.noListResultsText,
          }}
          light
        />
      )}
      {isLoading && (
        <div className="market-insight-statistic-archive-list__loading">
          <Loader />
        </div>
      )}
      {error && (
        <div className="market-insight-statistic-archive-list__error">
          {error}
        </div>
      )}
      {pager && (
        <div className="market-insight-statistic-archive-list__pager">
          <Pager {...pager} onPageChanging={onPageChange} />
        </div>
      )}
    </div>
  );
};

MarketInsightStatisticArchiveList.propTypes = {
  loadItemsEndpoint: PropTypes.string.isRequired,
  lang: PropTypes.shape({
    pdfLabel: PropTypes.string.isRequired,
    excelLabel: PropTypes.string.isRequired,
    noListResultsHeading: PropTypes.string.isRequired,
    noListResultsText: PropTypes.string.isRequired,
    serverErrorText: PropTypes.string.isRequired,
  }).isRequired,
};

export default MarketInsightStatisticArchiveList;
